import { z } from 'zod';
import { IsoTimestamp, UserId } from './common';

const NOTIFICATION_MODE_VALUES = ['always', 'errors_only', 'never'] as const;
export const NotificationMode = z.enum(NOTIFICATION_MODE_VALUES);
export type NotificationMode = z.infer<typeof NotificationMode>;

/** Forward-compat read variant — see #482. */
export const NotificationModeReadable = z
  .enum([...NOTIFICATION_MODE_VALUES, 'unknown'] as const)
  .catch('unknown');
export type NotificationModeReadable = z.infer<typeof NotificationModeReadable>;

const WORK_MODE_VALUES = ['remote', 'hybrid', 'onsite'] as const;
export const WorkMode = z.enum(WORK_MODE_VALUES);
export type WorkMode = z.infer<typeof WorkMode>;

/** Forward-compat read variant — see #482. */
export const WorkModeReadable = z.enum([...WORK_MODE_VALUES, 'unknown'] as const).catch('unknown');
export type WorkModeReadable = z.infer<typeof WorkModeReadable>;

const ENGAGEMENT_TYPE_VALUES = ['full_time_perm', 'contract', 'contract_to_perm'] as const;
export const EngagementType = z.enum(ENGAGEMENT_TYPE_VALUES);
export type EngagementType = z.infer<typeof EngagementType>;

/** Forward-compat read variant — see #482. */
export const EngagementTypeReadable = z
  .enum([...ENGAGEMENT_TYPE_VALUES, 'unknown'] as const)
  .catch('unknown');
export type EngagementTypeReadable = z.infer<typeof EngagementTypeReadable>;

export const ZipCode = z
  .string()
  .regex(/^\d{5}(-\d{4})?$/, 'Must be a US ZIP (12345 or 12345-6789)');

/**
 * A natural-language override rule the LLM evaluates against each job.
 * Examples: "IC role OK if base salary >= $200k", "Skip companies under
 * 50 people", "Always apply to roles mentioning Rust".
 *
 * Stored as plain text — the job-evaluator prompt receives the rule list
 * verbatim and weighs them when deciding fit.
 */
export const OverrideRule = z.object({
  id: z.string().min(1),
  rule: z.string().min(3).max(500),
  createdAt: IsoTimestamp,
});
export type OverrideRule = z.infer<typeof OverrideRule>;

/**
 * How the LinkedIn Easy Apply worker should pick (or upload) a resume
 * when the picker step appears mid-application.
 *
 *  - `most_recent`       — pick LinkedIn's most-recently-uploaded saved
 *                          resume (LinkedIn lists resumes in MRU order,
 *                          so this is the first tile). Falls through to
 *                          a fresh upload if zero resumes are attached.
 *  - `preferred_name`    — pick the saved resume whose name matches
 *                          `linkedInResumePreferredName` (case-insensitive,
 *                          exact). Falls back to most-recent + emits
 *                          `resume_preferred_name_missing` if the named
 *                          resume can't be found; falls through to upload
 *                          if zero resumes are attached.
 *  - `upload_ours`       — every cycle, upload the user's current resume
 *                          PDF from S3 fresh, regardless of what's
 *                          already attached. Use this when the user
 *                          wants the worker to always submit the latest
 *                          local copy and not trust LinkedIn's cached
 *                          versions.
 *  - `upload_if_missing` — DEFAULT. Use LinkedIn's most-recent saved
 *                          resume when at least one is attached;
 *                          otherwise upload ours. Safest behavior:
 *                          never overwrites a user's curated LinkedIn
 *                          resume list, but guarantees Easy Apply works
 *                          on a fresh LinkedIn account.
 */
const LINKEDIN_RESUME_STRATEGY_VALUES = [
  'most_recent',
  'preferred_name',
  'upload_ours',
  'upload_if_missing',
] as const;
export const LinkedInResumeStrategy = z.enum(LINKEDIN_RESUME_STRATEGY_VALUES);
export type LinkedInResumeStrategy = z.infer<typeof LinkedInResumeStrategy>;

/** Forward-compat read variant — see #482. */
export const LinkedInResumeStrategyReadable = z
  .enum([...LINKEDIN_RESUME_STRATEGY_VALUES, 'unknown'] as const)
  .catch('unknown');
export type LinkedInResumeStrategyReadable = z.infer<typeof LinkedInResumeStrategyReadable>;

export const Preferences = z.object({
  userId: UserId,

  // Title targeting
  titleKeywords: z.array(z.string()).default([]),
  titleExclusions: z.array(z.string()).default([]),

  // Location / commute
  locationPreferences: z.array(z.string()).default([]),
  zipCode: ZipCode.optional(),
  commuteMaxMiles: z.number().int().positive().max(500).optional(),
  workModes: z.array(WorkModeReadable).default(['remote']),

  // Compensation + level
  salaryMin: z.number().int().nonnegative().optional(),
  experienceLevels: z.array(z.string()).default([]),

  // Engagement type (perm/contract) — distinct from LinkedIn's coarse jobTypes filter.
  engagementTypes: z.array(EngagementTypeReadable).default(['full_time_perm']),
  // LinkedIn-side filter value, kept for compatibility ("Full-time"/"Contract"/etc.)
  jobTypes: z.array(z.string()).default(['Full-time']),

  // Hard exclusions
  companyBlacklist: z.array(z.string()).default([]),

  // LLM-evaluated override rules (free-form natural language).
  overrideRules: z.array(OverrideRule).default([]),

  // Caps. (Auto-scheduling was removed in #332 — every run is now
  // user-triggered via mobile "Run now" or the desktop tray. Existing
  // PROFILE/PREFS DDB rows with `cadenceHours` set are silently
  // dropped by Zod's default strip-on-parse behavior.)
  maxApplicationsPerCycle: z.number().int().positive().default(15),
  maxApplicationsPerDay: z.number().int().positive().default(50),

  // Notifications + control
  notificationMode: NotificationModeReadable.default('errors_only'),
  paused: z.boolean().default(false),

  // Onboarding progress — drives wizard-vs-manual UI affordance on mobile.
  onboardingCompletedAt: IsoTimestamp.optional(),

  // LinkedIn Easy Apply resume picker behavior. See LinkedInResumeStrategy
  // for the four modes. `upload_if_missing` is the safe default — works on
  // brand-new LinkedIn accounts without overwriting an existing curated
  // resume list on accounts that already have one.
  linkedInResumeStrategy: LinkedInResumeStrategyReadable.default('upload_if_missing'),
  // Required when strategy === 'preferred_name'. Matched
  // case-insensitively against the names LinkedIn shows in the picker
  // (typically the file basename, with or without the .pdf extension).
  linkedInResumePreferredName: z.string().max(255).optional(),

  /**
   * When the worker decides to apply to a job (passes pre-filters + LLM
   * fit check) but the apply attempt fails — Easy Apply form we can't
   * complete, external ATS we can't drive, missing email-verification
   * grant on a Workday tenant — should the worker click LinkedIn's
   * native "Save" button so the user can finish the application by hand
   * later?
   *
   * Default `true`. Saved jobs end up in
   * `linkedin.com/my-items/saved-jobs/`. Cost of saving is nearly zero
   * (one click, no anti-bot signal — Save is a feature LinkedIn
   * actively promotes), upside is a fallback queue of "things I almost
   * applied to" instead of losing them to the void. Set false if you'd
   * rather keep a clean saved-jobs list.
   */
  saveJobOnApplyFail: z.boolean().default(true),

  updatedAt: IsoTimestamp,
});

export type Preferences = z.infer<typeof Preferences>;

/**
 * PATCH body — overrides every widened read field back to the strict
 * enum so a client can't smuggle 'unknown' into DDB (#482). Same shape
 * as Preferences for the omitted fields, but the enums are strict.
 */
export const UpdatePreferencesRequest = Preferences.omit({
  userId: true,
  updatedAt: true,
})
  .extend({
    workModes: z.array(WorkMode),
    engagementTypes: z.array(EngagementType),
    notificationMode: NotificationMode,
    linkedInResumeStrategy: LinkedInResumeStrategy,
  })
  .partial();
export type UpdatePreferencesRequest = z.infer<typeof UpdatePreferencesRequest>;
